import { ReactNode } from 'react';
import { deceasedAdornment, deletedAdornment, PartyNameParty } from 'utils/partyNameUtil';
import { OpponentChip } from './OpponentChip';
import PersonaNonGrataChip from './PersonaNonGrataChip';

/**
 * Single source of truth for what the end adornment of a **contact dropdown option** looks like.
 * Used by every `ContactDynamic*` wrapper (form inputs, pickers, multi-selects, and the table
 * filter-piece). Keeps the following auto-wiring in one place so new contact dropdowns inherit it:
 *
 * - Caller-provided adornment (e.g. `renderPartyEmail`) — rendered first.
 * - `(Deceased)` text suffix when `party.flags.deceased` and partyType is PERSON.
 * - `(Deleted)` text suffix when `party.deletedAt` is set.
 * - `<PersonaNonGrataChip>` when `party.isPersonaNonGrata`.
 * - `<OpponentChip>` when `party.opponentCaseState` is set.
 */
export function buildContactEndAdornment(option: PartyNameParty, callerAdornment: ReactNode | null): ReactNode | null {
    const deceased = deceasedAdornment(option);
    const deleted = deletedAdornment(option);
    const showPersonaNonGrata = !!option.isPersonaNonGrata;

    if (!callerAdornment && !deceased && !deleted && !showPersonaNonGrata && !option.opponentCaseState) return null;

    return (
        <div className="inline-flex items-center gap-1">
            {callerAdornment}
            {deceased}
            {deleted}
            {showPersonaNonGrata && <PersonaNonGrataChip />}
            <OpponentChip state={option.opponentCaseState} />
        </div>
    );
}
